import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { SearchableCombobox } from '@/components/ui/searchable-combobox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Info, GraduationCap, Calendar, Clock, BookOpen, Compass } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import type { CohortEnrollment } from '@/hooks/useSelectedCohort';

export const ROUTES_TOOLTIP_TEXT =
  'Rutas de autoaprendizaje: avanzas a tu ritmo, cuando quieras, incluidas en tu plan. Rutas con acompañamiento: sesiones en vivo, mentoría y certificación al completar.';

interface CertificationHeaderProps {
  enrollments: CohortEnrollment[];
  selectedCohortId: string | null;
  onCohortChange: (cohortId: string) => void;
  showAllContentOption?: boolean;
  onSelectAllContent?: () => void;
  isAllContentSelected?: boolean;
}

export function CertificationHeader({
  enrollments,
  selectedCohortId,
  onCohortChange,
  showAllContentOption,
  onSelectAllContent,
  isAllContentSelected,
}: CertificationHeaderProps) {
  const [showInfoModal, setShowInfoModal] = useState(false);
  const navigate = useNavigate();

  const activeEnrollment = enrollments.find(e => e.cohort_id === selectedCohortId) || enrollments[0];

  if (!enrollments.length) return null;

  const formatDate = (d: string | null) => {
    if (!d) return '—';
    return format(parseISO(d), "d 'de' MMMM yyyy", { locale: es });
  };

  return (
    <>
      <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
        <div className="flex items-center gap-2">
          <GraduationCap className="w-5 h-5 text-primary flex-shrink-0" />

          {enrollments.length === 1 && !showAllContentOption ? (
            <span className="font-semibold text-foreground text-base truncate">
              {isAllContentSelected
                ? '📚 Todo el contenido'
                : activeEnrollment?.is_perpetual
                  ? `Ruta: ${activeEnrollment?.program_name} — Autoaprendizaje`
                  : `Ruta: ${activeEnrollment?.program_name} — ${activeEnrollment?.cohort_name}${activeEnrollment?.view_only ? ' (Mentor)' : ''}`}
            </span>
          ) : (
            <SearchableCombobox
              value={isAllContentSelected ? '__all__' : (selectedCohortId ?? '')}
              onSelect={(val) => {
                if (val === '__all__' && onSelectAllContent) {
                  onSelectAllContent();
                } else {
                  onCohortChange(val);
                }
              }}
              placeholder="Selecciona ruta"
              searchPlaceholder="Buscar ruta o cohorte..."
              triggerClassName="flex-1 min-w-0 border-none bg-transparent text-foreground font-semibold text-base hover:bg-muted/50 focus:ring-0 focus:ring-offset-0"
              options={[
                ...(showAllContentOption
                  ? [{ value: '__all__', label: '📚 Todo el contenido de la plataforma', keywords: ['todo', 'all', 'plataforma'] }]
                  : []),
                ...enrollments
                  .filter((e) => !e.view_only && !e.is_perpetual)
                  .map((e) => ({
                    value: e.cohort_id,
                    label: `Ruta: ${e.program_name} — ${e.cohort_name}`,
                    keywords: [e.program_name, e.cohort_name, 'acompañamiento'],
                    group: 'Rutas con acompañamiento',
                  })),
                ...enrollments
                  .filter((e) => !e.view_only && e.is_perpetual)
                  .map((e) => ({
                    value: e.cohort_id,
                    label: `Ruta: ${e.program_name} — Autoaprendizaje`,
                    keywords: [e.program_name, e.cohort_name, 'autoaprendizaje', 'self paced'],
                    group: 'Rutas de autoaprendizaje',
                  })),
                ...enrollments
                  .filter((e) => e.view_only)
                  .map((e) => ({
                    value: e.cohort_id,
                    label: `Ruta: ${e.program_name} — ${e.cohort_name} (Mentor)`,
                    keywords: [e.program_name, e.cohort_name, 'mentor', 'solo lectura'],
                    group: 'Cohortes donde eres mentor (solo lectura)',
                  })),

              ]}
            />
          )}

          <Button
            variant="ghost"
            size="icon"
            className="flex-shrink-0 text-muted-foreground hover:text-primary"
            onClick={() => setShowInfoModal(true)}
            title="Más información"
          >
            <Info className="w-4 h-4" />
          </Button>

          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-shrink-0 gap-1.5"
                  onClick={() => navigate('/rutas')}
                >
                  <Compass className="w-4 h-4" />
                  <span className="hidden sm:inline">
                    {isAllContentSelected ? 'Explorar rutas' : 'Explorar más rutas'}
                  </span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs text-xs leading-relaxed">
                {ROUTES_TOOLTIP_TEXT}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* Info Modal */}
      <Dialog open={showInfoModal} onOpenChange={setShowInfoModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-primary" />
              {activeEnrollment?.program_name}
            </DialogTitle>
            <DialogDescription className="pt-2">
              {activeEnrollment?.is_perpetual
                ? 'Ruta de autoaprendizaje — avanzas a tu propio ritmo'
                : `Cohorte: ${activeEnrollment?.cohort_name}`}
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4 pt-4">
            <div className="bg-muted/50 rounded-lg p-3">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Calendar className="w-4 h-4" />
                <span className="text-xs font-medium">Inicio</span>
              </div>
              <p className="text-sm font-semibold text-foreground">
                {formatDate(activeEnrollment?.start_date ?? null)}
              </p>
            </div>

            <div className="bg-muted/50 rounded-lg p-3">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Clock className="w-4 h-4" />
                <span className="text-xs font-medium">Fin</span>
              </div>
              <p className="text-sm font-semibold text-foreground">
                {formatDate(activeEnrollment?.end_date ?? null)}
              </p>
            </div>

            {activeEnrollment?.demo_day_date && (
              <div className="bg-muted/50 rounded-lg p-3 col-span-2">
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <BookOpen className="w-4 h-4" />
                  <span className="text-xs font-medium">Demo Day</span>
                </div>
                <p className="text-sm font-semibold text-foreground">
                  {formatDate(activeEnrollment.demo_day_date)}
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
